import React, { useState, useEffect } from 'react';
import { Upload, Button, message, Spin } from 'antd';
import { UploadOutlined, DeleteOutlined, LoadingOutlined, PlusOutlined } from '@ant-design/icons';
import * as Constants from '../Constants';
import ENVIRONMENT from '../../../../environments';

const { Dragger } = Upload;

const StyleGuidelinesUploadBox = ({ title, acceptedFormats, required = false, type = 'inspiration', onFileListChange, showError = false, initialFiles = [] }) => {
  const [fileList, setFileList] = useState([]);
  const [isPreviewHovered, setIsPreviewHovered] = useState(false);

  useEffect(() => {
    if (initialFiles && initialFiles.length > 0) {
      const converted = initialFiles.map(item => {
        const file = item.file_data;
        return {
          uid: file.uid,
          name: file.name,
          type: file.type || 'image/jpeg',
          status: 'done',
        };
      });
      setFileList(converted);
      if (onFileListChange) {
        onFileListChange(converted);
      }
    }
  }, [initialFiles]);

  const isImageMode = type === 'inspiration';
  const acceptedExtensions = isImageMode 
    ? ".jpg,.jpeg,.png,.tiff,.zip" 
    : ".pdf,.doc,.docx,.jpg,.jpeg,.zip";

  const supportedFormatsText = isImageMode 
    ? "Supported formats: JPG, PNG, TIFF or ZIP"
    : "Supported formats: PDF, DOC, JPG, or ZIP";

  const buttonText = isImageMode ? "Upload photos" : "Upload documents";

  const validateFile = (file) => {
    // Basic validation based on extension since MIME types can vary for zips/docs
    const extension = file.name.split('.').pop().toLowerCase();
    const allowedExtensions = acceptedExtensions.replace(/\./g, '').split(',');
    
    if (!allowedExtensions.includes(extension)) {
         // Fallback check for MIME types if needed, but extension check is usually sufficient for UI feedback
         // Check specific mime types if extension check is not enough or for stricter security
         if(isImageMode && !['image/jpeg', 'image/png', 'image/tiff', 'application/zip', 'application/x-zip-compressed'].includes(file.type)) {
             message.error(`File type not allowed. ${supportedFormatsText}`);
             return false;
         }
         if(!isImageMode && !['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'image/jpeg', 'application/zip', 'application/x-zip-compressed'].includes(file.type)) {
             // Allow jpg for documents too as per requirement
             message.error(`File type not allowed. ${supportedFormatsText}`);
             return false;
         }
    }
    return true;
  };

  const uploadProps = {
    ...Constants.upload_props,
    multiple: true,
    accept: acceptedExtensions,
    beforeUpload: (file) => {
      return validateFile(file);
    },
    fileList: fileList,
    showUploadList: false,
    onChange(info) {
      let newFileList = [...info.fileList];
      
      // Update state
      setFileList(newFileList);

      // Notify parent component
      if (onFileListChange) {
        onFileListChange(newFileList);
      }

      const { status } = info.file;
      if (status === "done") {
        message.success(`${info.file.name} file uploaded successfully.`);
      } else if (status === "error") {
        if (info.file.error && info.file.error.code && info.file.error.code.includes('Credentials')) {
          message.error('Your session has expired. Please reload the page.');
        } else {
          message.error(`${info.file.name} file upload failed.`);
        }
      }
    },
    onDrop(e) {
      console.log("Dropped files", e.dataTransfer.files);
    },
  };

  return (
    <div className="custom-child-container">
      <div className="manrope f-14 w-700 black">
        {title} {required && <span style={{ color: 'red' }}>*</span>}
      </div>
      <div style={{ marginTop: '10px' }}>
        {fileList.length === 0 ? (
          <Dragger
            {...uploadProps}
            style={{
              border: required && showError ? "1px dashed red" : "1px dashed #595959",
              background: "white",
              borderRadius: 8,
              padding: '21px 24px',
              textAlign: "center",
              minHeight: '140px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <div className="manrope" style={{ width: '100%' }}>
              <div style={{ color: "black", fontSize: '16px', marginBottom: '12px', fontWeight: '400' }}>
                Drag & Drop or
              </div>
              <Button icon={<UploadOutlined />} className="upload-photo-btn" style={{ marginBottom: '12px' }}>
                {buttonText}
              </Button>
              <div style={{ fontSize: 12, color: "#595959", lineHeight: '1.4' }}>
                {supportedFormatsText}
              </div>
            </div>
          </Dragger>
        ) : (
          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
             {/* Re-using the logic from CreateProductModal for file preview, but adapting for potential non-images */}
            {fileList.map((file) => {
                const isImage = file.type.startsWith('image/') || /\.(jpg|jpeg|png|webp|heic|tiff)$/i.test(file.name);
                return (
                    <div
                        key={file.uid}
                        style={{
                        position: 'relative',
                        width: '132px',
                        height: '132px',
                        border: '1px solid #D9D9D9',
                        borderRadius: '8px',
                        overflow: 'hidden',
                        backgroundColor: 'white',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                        }}
                        onMouseEnter={() => setIsPreviewHovered(file.uid)}
                        onMouseLeave={() => setIsPreviewHovered(null)}
                    >
                        {file.status === "uploading" ? (
                        <div style={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'center', 
                            height: '100%',
                            width: '100%',
                            backgroundColor: '#f5f5f5'
                        }}>
                            <Spin indicator={<LoadingOutlined spin />} style={{color: '#8C8C8C'}} size="large" />
                        </div>
                        ) : (
                        <>
                            <div style={{ width: '100%', height: '100%', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                {isImage ? (
                                    <img
                                        style={{
                                        width: '100%',
                                        height: '100%',
                                        objectFit: 'contain'
                                        }}
                                        // Constructing URL assuming AWS-like structure as in reference
                                        src={ENVIRONMENT.getBaseURL('aws') + encodeURIComponent("test_files" + "/" + file.uid + "/" + file.name)}
                                        alt={file.name}
                                    />
                                ) : (
                                    <div style={{ textAlign: 'center', padding: '5px' }}>
                                        <div className="manrope f-12 black" style={{ wordBreak: 'break-word' }}>{file.name}</div>
                                    </div>
                                )}
                            </div>
                            <Button
                            onClick={() => {
                                const newFileList = fileList.filter(item => item.uid !== file.uid);
                                setFileList(newFileList);
                                if (onFileListChange) {
                                  onFileListChange(newFileList);
                                }
                            }}
                            style={{
                                position: 'absolute',
                                top: '8px',
                                left: '8px',
                                width: '24px',
                                height: '24px',
                                minWidth: '24px',
                                padding: '0',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                backgroundColor: '#000000',
                                borderRadius: '4px',
                                border: 'none',
                                opacity: isPreviewHovered === file.uid ? 1 : 0,
                                transition: 'opacity 120ms ease-in-out'
                            }}
                            >
                            <DeleteOutlined style={{ color: 'white', fontSize: '12px' }} />
                            </Button>
                        </>
                        )}
                    </div>
                )
            })}
             {/* Add more button could go here if needed, but Dragger usually handles just dropping more or clicking again if not restricted */}
             <Dragger
                {...uploadProps}
                style={{
                  width: '132px',
                  height: '132px',
                  border: "1px dashed #595959",
                  background: "white",
                  borderRadius: 8,
                  textAlign: "center",
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <div className="manrope" style={{ width: '100%' }}>
                  <div style={{ color: "black", fontSize: '16px', marginBottom: '12px', fontWeight: '400' }}>
                    Drag & Drop or
                  </div>
                  <Button icon={<UploadOutlined />} className="upload-photo-btn" style={{ marginBottom: '12px' }}>
                    Upload
                  </Button>
                </div>
              </Dragger>
          </div>
        )}
      </div>
    </div>
  );
};

export default StyleGuidelinesUploadBox;
